import React from 'react';
import {useOutlet} from 'reconnect.js';
import * as User from 'rev.sdk.js/Actions/User';
import Config from '../../../data.json';
import AdminLayout from '../AdminLayout';
import NavBar from '../NavBar';
import ProfileLayout from '../ProfileLayout';
import SiteFooter from '../SiteFooter';
import CheckoutLayout from '../CheckoutLayout';
import useFacebookPixel from '../../Hooks/useFacebookPixel';
import useGoogleAnalytics from '../../Hooks/useGoogleAnalytics';
import useLayoutInit from '../../Hooks/useLayoutInit';
import PleaseWait from '../PleaseWait';
import useIsInApp from 'rev.sdk.js/Hooks/useIsInApp';
import useSiteNavBar from '../../Hooks/useSiteNavBar';
import useSiteFooter from '../../Hooks/useSiteFooter';
import * as CustomLayout from '../../Utils/CustomLayout';
import AboutLayout from '../AboutLayout';
import ListLayout from '../ListLayout';
import MemberLayout from '../MemberLayout';
import AppLayout from '../AppLayout';
import EngLayout from '../EngLayout';
import EngNavBar from '../EngNavBar';
import EngFooter from '../EngFooter';
import useArea from '../../Hooks/useArea';

function Layout(props) {
  const {children, location} = props;
  const isInApp = useIsInApp();
  const [initialized, setInitialized] = React.useState(false);
  const {initialArea} = useArea();

  useFacebookPixel({pixelId: Config.pixelId});
  useGoogleAnalytics({gaId: Config.gaId, gtmId: Config.gtmId});

  const {extraElement} = useLayoutInit(props);
  const {showNavbar} = useSiteNavBar({
    location: props.location,
    pageContext: props.pageContext,
  });
  const {showFooter} = useSiteFooter({
    location: props.location,
    pageContext: props.pageContext,
  });

  React.useEffect(() => {
    const isAdmin = location.pathname.indexOf('admin') !== -1;
    const isApp = location.pathname.indexOf('/app/') !== -1;

    const initialize = async (isAdmin) => {
      let result = false;

      try {
        result = await User.autoLogin({admin: isAdmin});
      } catch (ex) {
        console.log('autoLogin ex', ex);
        User.logout();
      }

      if (Config.allowAnonymousCheckout && !result) {
        await User.tempLogin();
      }

      setInitialized(true);
    };

    if (!initialized && !isApp) {
      initialize(isAdmin);
    }
  }, [location.pathname, initialized]);

  React.useEffect(() => {
    const isAdmin = location.pathname.indexOf('admin') !== -1;

    if (initialized && isAdmin) {
      let timer = setInterval(async () => {
        console.log('auto refresh access token');
        try {
          await User.autoLogin({admin: isAdmin});
        } catch (ex) {
          console.log('autoLogin ex', ex);
          User.logout();
        }
      }, 7080000); // 1 hour 58 minute

      return () => clearInterval(timer);
    }
  }, [location.pathname, initialized]);

  React.useEffect(() => {
    setTimeout(() => {
      initialArea({location: props.location});
    }, 0); // fix PageContext cannot listen changed
  }, [initialArea, props.location?.pathname]);

  if (location.pathname === '/') {
    return children;
  } else if (location.pathname.indexOf('admin') !== -1) {
    if (!initialized) {
      return <PleaseWait title="自動登入中" />;
    }

    return (
      <>
        {CustomLayout.renderHtmlHelmet({location})}
        <AdminLayout location={location}>{children}</AdminLayout>
      </>
    );
  } else if (
    location.pathname.indexOf('profile') !== -1 ||
    location.pathname.indexOf('/doctor/member') !== -1
  ) {
    if (!initialized) {
      return <PleaseWait title="自動登入中" />;
    }

    return (
      <>
        {CustomLayout.renderHtmlHelmet({location})}
        <NavBar {...props} />
        <ProfileLayout style={{flex: 1}} location={location}>
          {children}
        </ProfileLayout>
        <SiteFooter {...props} />
      </>
    );
  } else if (location.pathname.indexOf('/about') > 0) {
    return (
      <>
        {CustomLayout.renderHtmlHelmet({location})}
        {!isInApp && <NavBar {...props} />}
        <AboutLayout {...props}>{children}</AboutLayout>
        {!isInApp && <SiteFooter {...props} />}
        {extraElement}
      </>
    );
  } else if (props.pageContext.layout === 'list') {
    return (
      <>
        {CustomLayout.renderHtmlHelmet({location})}
        {!isInApp && <NavBar {...props} />}
        <ListLayout {...props}>{children}</ListLayout>
        {!isInApp && <SiteFooter {...props} />}
        {extraElement}
      </>
    );
  } else if (location.pathname.indexOf('/app/') === 0) {
    return (
      <>
        {CustomLayout.renderHtmlHelmet({location})}
        <AppLayout {...props}>{children}</AppLayout>
        {extraElement}
      </>
    );
  }

  return (
    <>
      {CustomLayout.renderHtmlHelmet({location})}
      {showNavbar && <NavBar {...props} />}
      <div style={{flex: 1}}>{children}</div>
      {showFooter && <SiteFooter {...props} />}
      {extraElement}
    </>
  );
}

export default Layout;
